import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { LivechatService } from '@app/livechat/livechat.service';
import { CertificateService } from '@app/services/certificates/certificate.service';
import { ErrorService } from '@app/services/error.service';
import { OAuth2Service } from '@app/services/oauth2.service';

import { SessionService } from '@app/services/session/session.service';
import { UserCertificate, UserSession } from '@app/store/models';
import { selectCertificate } from '@app/store/selectors/certificate.selector';
import {
  isCertValid,
  isLogged,
  needsForcePassword,
  selectSession,
} from '@app/store/selectors/session.selector';
import { GlobalState } from '@app/store/store';
import { environment } from '@env/environment';
import {
  Brand,
  BreadcrumbItem,
  DropdownItem,
  HeaderCategoryItem,
  INavigationLink,
  IStepperItem,
} from '@infocert/ng-ui-kit/models';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BsDropdownConfig } from 'ngx-bootstrap/dropdown';

import { CookieService } from 'ngx-cookie-service';
import { LocalStorageService } from 'ngx-webstorage';
import { combineLatest, filter, map, Observable, of } from 'rxjs';
import { I18nService, languagesMap } from 'src/app/translations/i18n';

enum UserItemEnum {
  MANAGE_ACCOUNT,
  LANGUAGE,
  LOGOUT,
}

@UntilDestroy()
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  providers: [
    {
      provide: BsDropdownConfig,
      useValue: { isAnimated: true, autoClose: true },
    },
  ],
  standalone: false,
})
export class HeaderComponent implements OnInit, AfterViewInit {
  constructor(
    private i18nService: I18nService,
    private sessionService: SessionService,
    private oauth2Service: OAuth2Service,
    private router: Router,
    private certificateService: CertificateService,
    private store: Store<GlobalState>,
    private cookieService: CookieService,
    private localStorageService: LocalStorageService,
    private _translateService: TranslateService,
    private livechatService: LivechatService,
    private errorService: ErrorService
  ) {}

  brand: Brand = {
    name: 'MySign',
    style: 'default',
    // Nota: logo e alt non sono supportati dal tipo Brand corrente
    // L'accessibilità verrà gestita tramite configurazione CSS o altra strategia
  };
  @ViewChild('headerRef', { read: ElementRef }) headerRef!: ElementRef;
  logged$: Observable<boolean>;
  isCertValid$: Observable<boolean>;
  needsForcePassword$: Observable<boolean>;
  userSession$: Observable<UserSession>;
  userCertificate$: Observable<UserCertificate>;
  userInfoLoaded = false;
  private logoElement: HTMLElement | null = null;

  currentUrl: string = '';
  error_code: string;
  error_message: string;
  correlationIdMessage: string;

  languages$: Observable<DropdownItem[]> = of([]);
  userItems$: Observable<DropdownItem[]>;

  ngOnInit(): void {
    this.userSession$ = this.store.select(selectSession);
    this.userCertificate$ = this.store
      .select(selectCertificate)
      .pipe(
        filter(
          (certificate): certificate is UserCertificate =>
            certificate !== undefined
        )
      );
    this.logged$ = this.store.select(isLogged);
    this.isCertValid$ = this.store.select(isCertValid);
    this.needsForcePassword$ = this.store.select(needsForcePassword);

    //resta in ascolto sui cambiamenti del routing pagina corrente
    this.routeListener();

    //controlla session e certificate quando viene ricaricata la pagina
    this.checkSession();

    this.userHeader();

    this.initCategoriesDropdown();

    this._translateService.onLangChange
      .pipe(untilDestroyed(this))
      .subscribe(() => this.setLogoAccessibilityLabels());
  }

  ngAfterViewInit(): void {
    // Cerca all'interno del componente ng-ui-kit-header l'elemento che rappresenta il logo
    // usando come selettore la classe CSS specifica trovata nel DOM (.ic1-brand-infocert-default)
    this.logoElement = this.headerRef?.nativeElement?.querySelector(
      '.ic1-brand-infocert-default'
    );

    // Stampa in console se l'elemento è stato trovato (utile per il debug)
    console.log('Logo trovato:', this.logoElement);

    // Se l'elemento del logo è stato trovato correttamente nel DOM...
    if (this.logoElement) {
      // Rimuove cursor:pointer per accessibilità (l'elemento ha già listener click)
      this.logoElement.style.cursor = 'default';
      this.setLogoAccessibilityLabels();

      // Aggiunge un listener al click sull'elemento logo
      // Quando l'utente clicca sul logo, viene chiamato il metodo onTitleClicked()
      this.logoElement.addEventListener('click', () => {
        this.onLogoClicked(); // Esegue il redirect alla homepage o pagina multi-cert
      });
    }
  }

  clickedLanguage() {
    this.livechatService.toggleChatBot('close');
  }

  private setLogoAccessibilityLabels(): void {
    if (!this.logoElement) {
      return;
    }

    const logoAltText =
      this._translateService.instant('HEADER.LOGO_ALT') || 'MySign';

    const imgElement = this.logoElement.querySelector('img');

    if (imgElement) {
      imgElement.setAttribute('alt', logoAltText);
    } else {
      this.logoElement.setAttribute('aria-label', logoAltText);
      this.logoElement.setAttribute('role', 'img');
    }
  }

  userHeader() {
    combineLatest([
      this.isCertValid$,
      this.needsForcePassword$,
      this.userSession$,
      this.userCertificate$,
    ])
      .pipe(untilDestroyed(this))
      .subscribe(
        ([isCertValid, needsForcePassword, userSession, userCertificate]) => {
          if (!userSession || !userCertificate) {
            this.userInfoLoaded = false;
            return;
          }

          this.userInfoLoaded = true;

          if (userSession && userCertificate) {
            const keys = [
              'HEADER.MANAGE_ACCOUNT',
              'HEADER.CHANGE_PWD.TITLE',
              'HEADER.LOGOUT',
              ...this.i18nService.languagesKeyTranslations,
            ];

            let manage_message = 'HEADER.CHANGE_PWD.TITLE';
            if (userSession.isacUser) {
              manage_message = 'HEADER.MANAGE_ACCOUNT';
            }

            let needActivation = [];
            if (this.localStorageService.retrieve('needActivation')) {
              if (userCertificate.certificateStatus === 'EMITTED') {
                let filteredNeedActivation = this.localStorageService
                  .retrieve('needActivation')
                  .filter(elem => elem !== this.certificateService.certID);
                this.localStorageService.store(
                  'needActivation',
                  filteredNeedActivation
                );
              }
              needActivation =
                this.localStorageService.retrieve('needActivation');
            }

            if (
              isCertValid &&
              !needActivation.includes(userCertificate.certId) &&
              !needsForcePassword
            ) {
              this.userItems$ = this._translateService.stream(keys).pipe(
                map(res => {
                  return [
                    {
                      id: UserItemEnum.MANAGE_ACCOUNT,
                      label: res[manage_message],
                    },
                    /*{
                      id: UserItemEnum.LANGUAGE,
                      label: this.languageLabel,
                      iconBefore: 'language',
                      items: this.i18nService.languages2chars.map(lang => ({
                        id: UserItemEnum.LANGUAGE,
                        value: lang,
                        flag: '',
                        label:
                          res[`LANGUAGE.BUTTON_LANG_${lang.toUpperCase()}`],
                        selected:
                          this.i18nService.language === languagesMap[lang],
                        onClick: () => {
                          this.setLanguage(lang);
                        },
                      })),
                    },*/
                    {
                      id: UserItemEnum.LOGOUT,
                      label: res['HEADER.LOGOUT'],
                      iconBefore: 'logout',
                    },
                  ] as DropdownItem[];
                })
              );
            } else {
              this.userItems$ = this._translateService.stream(keys).pipe(
                map(res => {
                  return [
                    {
                      id: UserItemEnum.LANGUAGE,
                      label: this.languageLabel,
                      iconBefore: 'language',
                      items: this.i18nService.languages2chars.map(lang => ({
                        id: UserItemEnum.LANGUAGE,
                        value: lang,
                        flag: '',
                        label:
                          res[`LANGUAGE.BUTTON_LANG_${lang.toUpperCase()}`],
                        selected:
                          this.i18nService.language === languagesMap[lang],
                        onClick: () => {
                          this.setLanguage(lang);
                        },
                      })),
                    },
                    {
                      id: UserItemEnum.LOGOUT,
                      label: res['HEADER.LOGOUT'],
                      iconBefore: 'logout',
                    },
                  ] as DropdownItem[];
                })
              );
            }

            this.languages$ = this._translateService.stream(keys).pipe(
              map(
                res =>
                  this.i18nService.languages2chars.map(lang => ({
                    id: UserItemEnum.LANGUAGE,
                    value: lang,
                    flag: '',
                    label: res[`LANGUAGE.BUTTON_LANG_${lang.toUpperCase()}`],
                    selected: this.i18nService.language === languagesMap[lang],
                    // onClick: () => {
                    //   this.setLanguage(lang);
                    // },
                  })) as DropdownItem[]
              )
            );
          }
        }
      );
  }
  onLanguageChanged(item: DropdownItem) {
    console.log('🚀 ~ HeaderComponent ~ onChangeLanguage ~ item', item);
    //salvo la lingua corrente nello storage
    localStorage.setItem('current_language', item.value);
    this.setLanguage(item.value);
    this.showError();
  }

  get languageLabel(): string {
    return this.i18nService.languageLabel;
  }

  onLogoClicked(): void {
    console.log('Logo clicked');
    if (!this.localStorageService.retrieve('isMulticert')) {
      this.router.navigate(['/homepage']);
    } else {
      this.router.navigate(['/multi-cert']);
    }
  }

  onUserClicked(item: DropdownItem): void {
    if (item.id === UserItemEnum.MANAGE_ACCOUNT) {
      const isLocalOrCL =
        window.location.host.includes('mysigncl') ||
        window.location.host.includes('localhost');

      if (isLocalOrCL) {
        console.log('[MyAccount] LOCAL OR CL');
        window.open('https://myaccountcl.infocert.it?prompt=none', '_blank');
      } else {
        console.log('[MyAccount] PROD');
        window.open('https://myaccount.infocert.it?prompt=none', '_blank');
      }
    } else if (item.id === UserItemEnum.LOGOUT) {
      //setto la lingua del browser prima del logout
      this.setLanguage(navigator.language);
      this.sessionService.logout();
    } else if (item.id === UserItemEnum.LANGUAGE) {
      this.setLanguage(item.value);
    }
  }

  routeListener() {
    this.router.events
      .pipe(
        filter((e: Event): e is NavigationEnd => e instanceof NavigationEnd)
      )
      .subscribe((routerEvent: NavigationEnd) => {
        this.currentUrl = routerEvent.url;
      });
  }

  async checkSession() {
    // Deep-link BCC: non deve essere intercettato dalla session check dell'header.
    // Il flusso federato viene gestito da BccFederationComponent/OAuth2Service.
    if (window.location.hash.startsWith('#/bcc')) {
      return;
    }

    const hasCode = new URLSearchParams(window.location.search).has('code');

    // Se siamo nel callback ISAC (URL con ?code=...), lasciamo che OAuth2Service.tryLogin()
    // completi lo scambio del code con i token e non tocchiamo la sessione qui.
    if (hasCode) {
      return;
    }

    // fa le chiamate se il cookie di session esiste, fa logout se non esiste il cookie)
    const hasCookie = !!this.cookieService.get('oauth-access-token');
    const hasValidTokens = this.oauth2Service.hasValidTokens;
    const blockAutoLoginAfterLogout =
      sessionStorage.getItem('blockAutoLoginAfterLogout') === 'true';

    // Se l'utente ha fatto logout, blocchiamo l'auto-login su semplice refresh:
    // in quel caso non consideriamo validi i token OIDC eventualmente rimasti.
    // Il login esplicito o un callback OIDC valido rimuovono questa flag.
    if (hasCookie || (!blockAutoLoginAfterLogout && hasValidTokens)) {
      if (location.hash.includes('identity')) {
        console.log('identity found');
        localStorage.setItem('identity', 'redirect');
      }
      this.router.navigate(['/oauth2'], {
        preserveFragment: true,
      });
    } else {
      // Facciamo il logout in tutti gli ambienti quando manca il cookie
      this.sessionService.logoutNoSession();
    }
  }

  setLanguage(language: string) {
    this.i18nService.language = language;
  }

  get currentLanguage(): string {
    return this.i18nService.language || 'it_IT';
  }

  get prefixes(): string[] {
    return this.i18nService.supportedLanguages;
  }

  // get languages(): DropdownItem[] {
  //   const langs = this.i18nService.supportedLanguages.map((langCode, index) => {
  //     const langKey = `LANGUAGE.BUTTON_LANG_${langCode
  //       .split('_')[0]
  //       .toUpperCase()}`;
  //     const item: DropdownItem = {
  //       label: this._translateService.instant(langKey),
  //       selected: index == 0,
  //     };
  //     return item;
  //   });
  //   //console.log('🚀 ~ HeaderComponent ~ langs ~ langs', langs);
  //   return langs;
  // }

  openUrl(type: 'support') {
    window.open(
      this._translateService.instant('URLS.CONTACT_SUPPORT_URL'),
      '_blank'
    );
  }

  //se l'errorCode è esistente nello storage c'è un toast non chiuso, quindi lo riapro tradotto
  showError() {
    this.error_code = sessionStorage.getItem('errorCode');
    if (this.error_code) {
      //setto la variabile del cambio lingua dell'errorService a true
      this.errorService.updateLanguageChanged(true);
      setTimeout(() => {
        this.error_message = this._translateService.instant(
          `SERVER_ERRORS.${this.error_code}`
        );
        this.correlationIdMessage = this._translateService.instant(
          'ERROR_MODAL.ERROR_CODE',
          {
            errorCode: localStorage.getItem('correlationId'),
          }
        );
        this.errorService.showError(
          this.error_message,
          this.error_code,
          this.correlationIdMessage
        );
      }, 400);
    }
  }

  breadcrumb: BreadcrumbItem[] = [
    { label: 'Previous Page' },
    { label: 'Previous Page' },
    { label: 'Current Page' },
  ];

  items: DropdownItem[] = [
    { label: 'Lorem ipsum' },
    { label: 'Lorem ipsum' },
    { label: 'Logout', iconBefore: 'logout' },
  ];

  categories: HeaderCategoryItem[] = [
    { label: 'PEC' },
    { label: 'Firma Digitale', routerLink: '/placeholder/route' },
    { label: 'Fatturazione Elettronica' },
    { label: 'SPID' },
    { label: 'Conservazione' },
    { label: 'Informazioni commerciali' },
    { label: 'SecureDrive', href: 'https://www.google.it' },
    {
      label: 'Scopri le soluzioni',
      onClick: () => console.log('scopri le istruzioni'),
    },
  ];

  categoriesDropdown: HeaderCategoryItem[];

  links: INavigationLink[] = [
    { id: '1', label: 'Link 1', routerLink: 'page/1' },
    { id: '2', label: 'Link 2', routerLink: 'page/2' },
    { id: '3', label: 'Link 3', routerLink: 'page/3' },
  ];

  languages: DropdownItem[] = [
    { label: 'Francese' },
    { label: 'Inglese' },
    { label: 'Italiano', selected: true },
    { label: 'Spagnolo' },
    { label: 'Tedesco' },
  ];

  stepperItems: IStepperItem[] = [
    { id: 'step-1', label: 'Step 1', complete: true },
    { id: 'step-2', label: 'Step 2', active: true },
    { id: 'step-3', label: 'Step 3' },
    { id: 'step-4', label: 'Step 4' },
    { id: 'step-5', label: 'Step 5' },
  ];

  initCategoriesDropdown(): void {
    this.categoriesDropdown = [
      {
        label: 'PEC',
        items: [
          { label: 'Lorem ipsum', onClick: () => console.log('PEC click') },
        ],
      },
      {
        label: 'Firma Digitale',
        items: [
          {
            label: 'Lorem ipsum',
            onClick: () => console.log('Firma Digitale click'),
          },
        ],
      },
      {
        label: 'Fatturazione Elettronica',
        items: [
          {
            label: 'Lorem ipsum',
            onClick: () => console.log('Fatturazione Elettronica click'),
          },
        ],
      },
      { label: 'SPID' },

      { label: 'Informazioni commerciali' },
    ];
  }

  onClickTitle(): void {
    console.log('Title click');
  }

  onPreviousStep(event: IStepperItem): void {
    console.log('Previous step', event);
  }
}
